import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { czytajHistorie, filtryZWpisu, usunWpis, wyczyscHistorie, type WpisHistorii } from '@/lib/historiaWyszukiwan';
import { HistoriaWyszukiwan } from './HistoriaWyszukiwan';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PRIMARY_ACTIVE_CITY } from '@/lib/geoScope';
import { Building2, Home, MapPin, Search, X } from 'lucide-react';
import { WyborDzielnic } from './WyborDzielnic';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { MAX_OBSZAROW, dodajObszar, dzielnicaObszaru } from '@/lib/areaSelection';
import { useZapisBrakuPodpowiedzi } from '@/lib/brakiWyszukiwania';
import { WarsawMetroIcon } from '@/components/offer/WarsawMetroBadge';
import { areaKey, etykietaObszaru, nazwaObszaru, searchAreas, type SearchArea } from '@/lib/areaSearch';
import { useDotyk, useIsDesktop } from '@/hooks/use-mobile';
import ScrollableRow from './ScrollableRow';
import SearchAreaOverlay from './SearchAreaOverlay';
import { ListaMiejscWPolu, WierszSzukajMiejsca } from './MiejscaWPolu';
import type { SourcePostFilters } from '@/hooks/useSourcePosts';
import { useOstatnieMiejsca, type Szukaj } from '@/hooks/useBliskoDo';
import { useMiejsceWPolu } from '@/hooks/useMiejsceWPolu';
import { obszarZMiejsca, type PodpowiedzMiejsca } from '@/lib/bliskoDo';
import { przesunAktywny } from '@/lib/miejsceWPolu';
import { klasyZastapienia, useBlyskZastapienia } from '@/hooks/useBlyskZastapienia';

interface SearchAreaBarProps {
  areas: SearchArea[];
  onChange: (next: SearchArea[]) => void;
  /** Telefon: przejście z nakładki do rysowania obszaru na mapie. */
  onDrawArea?: () => void;
  /** Telefon: przejście z nakładki do klikania dzielnic na mapie. */
  onPickDistricts?: () => void;
  /** Pozostałe filtry — nakładka liczy z nich wynik na żywo. */
  filters?: SourcePostFilters;
  /**
   * Podane tylko, gdy wybrana jest lokalizacja: na końcu pola staje × „wróć do całego miasta"
   * (28.09 — „Wyczyść filtry" lokalizacji już nie zdejmuje, więc potrzebny jest osobny gest).
   */
  naWyczyscLokalizacje?: () => void;
  /** Wybór wpisu z historii wyszukiwań — przywraca cały stan wyszukiwania. */
  onPrzywroc?: (f: SourcePostFilters) => void;
  /** Wyszukiwanie miejsc (domyślnie `szukajMiejsc` → `place-autocomplete`); testy podstawiają atrapę. */
  szukajMiejsc?: Szukaj;
}

// Area chips shown inline in the bar; the rest collapse into a "+N" popover so
// the field never grows past one row (a map district-pick can add 8+ at once).
const MAX_INLINE_AREAS = 2;

type Translate = (key: string, opts?: Record<string, unknown>) => string;

/**
 * One removable area chip — reused inline in the bar (both szerokości) and in the
 * "+N" popover.
 *
 * `onBody` robi z etykiety przycisk otwierający nakładkę. Podaje go TYLKO telefon:
 * tam pole nie ma inputu, więc dotknięcie chipa musi gdzieś prowadzić. Na pulpicie
 * etykieta zostaje zwykłym tekstem — obok stoi input i to on jest celem.
 *
 * Krzyżyk i etykieta są RODZEŃSTWEM, nigdy przyciskiem w przycisku: zagnieżdżony
 * przycisk to nieprawidłowy HTML, a przeglądarka sama rozstrzyga, który z nich
 * dostaje dotknięcie.
 */
function AreaChip({
  area,
  onRemove,
  onBody,
  sciskalny,
  przygaszony,
  blysk,
  t,
}: {
  area: SearchArea;
  onRemove: () => void;
  onBody?: () => void;
  /** Najechanie na podpowiedź, która ten obszar zastąpi: chip przygasa i jest przekreślony (prototyp 19.10). */
  przygaszony?: boolean;
  /** Ten obszar właśnie zastąpił inny: krótko podświetlony w miejscu poprzednika, bez komunikatu. */
  blysk?: boolean;
  /**
   * Pulpit, chip w jednym rzędzie pola: chip MOŻE się ścisnąć (nazwa z wielokropkiem), żeby
   * „Dzielnice” i × na końcu pola nigdy nie wypadły poza `overflow-hidden` — przy dwóch chipach
   * i „+6” wypadały (podgląd 15.10). Telefon przewija rząd w poziomie, więc tam chip zostaje sztywny.
   */
  sciskalny?: boolean;
  t: Translate;
}) {
  // Osiedle niesie swoją dzielnicę, jak w makiecie („Muranów · Śródmieście”) i w panelu.
  const dzielnica = area.kind === 'subdistrict' ? dzielnicaObszaru(area) : null;
  const label = (
    <>
      {etykietaObszaru(area, t)}
      {dzielnica && <span className="font-normal opacity-70"> · {dzielnica}</span>}
    </>
  );
  const ikona =
    area.kind === 'station' ? (
      <WarsawMetroIcon size={16} />
    ) : area.kind === 'place' ? (
      <MapPin className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden />
    ) : null;
  return (
    <span
      data-testid="chip-obszaru"
      data-przygaszony={przygaszony || undefined}
      data-blysk={blysk || undefined}
      className={`flex items-center gap-1 rounded-full py-1.5 pl-3 pr-1.5 text-sm font-medium lg:gap-1.5 ${klasyZastapienia({
        przygaszony,
        blysk,
        tlo: 'bg-muted',
      })} ${sciskalny ? 'min-w-0 shrink' : 'shrink-0'}`}
    >
      {onBody ? (
        <button type="button" onClick={onBody} className="flex items-center gap-1 lg:gap-1.5">
          {ikona}
          {label}
        </button>
      ) : (
        <>
          {ikona}
          <span className={sciskalny ? 'min-w-0 truncate' : undefined}>{label}</span>
        </>
      )}
      {/* Cel dotknięcia powiększony pseudoelementem, a nie paddingiem: padding
          rozepchnąłby chip tak, że na 393 px zmieściłby się jeden zamiast dwóch.
          Podział 8 px paddingu + 8 px pseudoelementu daje zmierzone 44×44 przy
          wycieku tylko 4 px na etykietę obok; sam pseudoelement (0 + 16) wyciekał
          12 px, czyli na ostatnią literę nazwy. Zmierzone sondowaniem
          elementFromPoint w scripts/check-pasek-obszarow.mjs — prostokąt przycisku
          tego nie pokaże, bo pseudoelementu w nim nie ma. `-my-1` zdejmuje z chipa
          wysokość, którą dołożył padding: bez tego pasek urósł z 54 na 58 px, czyli
          oddalibyśmy w nagłówku tyle, ile właśnie w nim odzyskaliśmy. */}
      <button
        type="button"
        aria-label={t('search.area.remove', { name: label })}
        onClick={onRemove}
        className="relative -my-1 p-2 text-muted-foreground after:absolute after:-inset-2 after:content-[''] hover:text-foreground"
      >
        <X className="h-3 w-3 lg:h-3.5 lg:w-3.5" />
      </button>
    </span>
  );
}

/** Poniżej tylu pikseli pasek dostaje krótszą podpowiedź (pomiar: 1440 → 569 px, 1280 → 409 px). */
const PROG_KROTKIEJ_PODPOWIEDZI = 480;

/**
 * Area chips + local autocomplete (districts by name, estates by OSM bbox —
 * src/lib/areaSearch.ts; no network). Strict-select like
 * AddressAutocompleteInput: free text never becomes a filter. Places (a
 * university, an office, an address) come from the last row „Szukaj miejsca”
 * (plan §1.90): one `place-autocomplete` call when that row is chosen, never
 * per keystroke; the pick is the same `kind: 'place'` area „Blisko do…” sets.
 * The glow after a replacement comes from `useBlyskZastapienia` (shared with
 * the district list, the overlay and the panel's Lokalizacja row).
 */

const SearchAreaBar = ({ areas, onChange, onDrawArea, onPickDistricts, filters, naWyczyscLokalizacje, onPrzywroc, szukajMiejsc }: SearchAreaBarProps) => {
  const { t } = useTranslation();
  // Nazwa miasta w języku widza — ten sam klucz co nakładka obszaru (SearchAreaOverlay), budowany
  // z identyfikatora miasta, nie literał (Konwencja 1 i 2; Konwencja 23 dotyczy list miast).
  // Do 30.09 było tu wpisane „Warszawa”, więc ua/by widziały łacinkę obok „Варшава” w nakładce.
  const nazwaMiasta = t(`search.area.miasto_mianownik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY });
  const isDesktop = useIsDesktop();
  /**
   * TABLET DOSTAJE NAKŁADKĘ, choć ma układ pulpitowy (zgłoszenie Piotra z iPada, 28.09):
   * klawiatura zasłaniała ~2/3 ekranu i podpowiedzi pod polem u góry strony były
   * praktycznie niewidoczne. Decyduje sposób obsługi (`useDotyk`), nie szerokość —
   * pulpit z myszką zostaje przy polu do pisania.
   */
  const dotyk = useDotyk();
  const trybNakladki = !isDesktop || dotyk;
  // Pole z trybem miejsc (plan §1.90): `query` i to, czy lista pokazuje wyniki miejsc zamiast
  // dzielnic. Wyszukiwanie miejsc nie leci przy pisaniu — dopiero po wyborze wiersza „Szukaj miejsca”.
  const pole = useMiejsceWPolu(szukajMiejsc);
  const { query } = pole;
  const ostatnieMiejsca = useOstatnieMiejsca();
  const [isOpen, setIsOpen] = useState(false);
  const [warsawInfoOpen, setWarsawInfoOpen] = useState(false);
  const [overlayOpen, setOverlayOpen] = useState(false);
  /** Dymek „Dzielnice” (pulpit) — ta sama kaskada, co w nakładce na telefonie i w panelu. */
  const [dzielniceOtwarte, setDzielniceOtwarte] = useState(false);

  const selected = new Set(areas.map(areaKey));
  // Każda podpowiedź od razu wie, co jej wybór zrobi z listą (reguły z areaSelection.ts): które
  // chipy zastąpi i czy nie przekroczy limitu 8. Pole stosuje te same reguły, co lista dzielnic,
  // panel i nakładka — do 19.10 jako jedyne dopisywało obszar na koniec bez reguł, więc
  // „Rembertów” i „Nowy Rembertów” mogły stać obok siebie (zgłoszenie Maćka).
  const suggestions = searchAreas(query)
    .filter((s) => !selected.has(areaKey(s.area)))
    .map((s) => ({ ...s, wynik: dodajObszar(areas, s.area) }));
  const ponadLimit = suggestions.some((s) => !s.disabled && s.wynik.limitOsiagniety);
  /** Klucz podpowiedzi pod kursorem — jej `zastapione` przygasają chipy w polu. */
  const [podglad, setPodglad] = useState<string | null>(null);
  /** Wynik miejsca pod kursorem — przygasza chip miejsca, które zastąpi. */
  const [podgladMiejsca, setPodgladMiejsca] = useState<PodpowiedzMiejsca | null>(null);
  /**
   * Pozycja podświetlona strzałkami. Lista dzielnic: 0…n-1 podpowiedzi, n = „Szukaj miejsca”.
   * Tryb miejsc: 0 = „wróć”, 1… = wyniki. -1 = nic (start i po każdej zmianie listy).
   */
  const [aktywny, setAktywny] = useState(-1);
  const kluczAktywnej = !pole.trybMiejsca && aktywny >= 0 && aktywny < suggestions.length ? areaKey(suggestions[aktywny].area) : null;
  /** Dymek „Dzielnice”: chipy, które zastąpiłaby dzielnica albo osiedle pod kursorem (plan §1.91). */
  const [podgladDzielnic, setPodgladDzielnic] = useState<string[]>([]);
  const przygaszone = new Set([
    ...(pole.trybMiejsca
      ? podgladMiejsca
        ? dodajObszar(areas, obszarZMiejsca(podgladMiejsca)).zastapione
        : []
      : (suggestions.find((s) => areaKey(s.area) === (podglad ?? kluczAktywnej))?.wynik.zastapione ?? [])),
    ...podgladDzielnic,
  ]);
  const blysk = useBlyskZastapienia(areas);
  const showDropdown = isOpen && query.trim().length >= 2;
  // Fraza bez ani jednej podpowiedzi idzie do przeglądu braków (flat.area_search_misses, 19.10).
  useZapisBrakuPodpowiedzi(query, showDropdown && suggestions.length === 0);
  /**
   * HISTORIA WYSZUKIWAŃ pod polem (pulpit z myszą): kliknięcie w PUSTE pole pokazuje ostatnie
   * wyszukiwania. Czytana przy każdym otwarciu — między otwarciami mogła się zmienić (nowy
   * zapis po „Pokaż N ofert").
   */
  const [historia, setHistoria] = useState<WpisHistorii[]>([]);
  const pokazHistorie = isOpen && query.trim() === '' && historia.length > 0 && !!onPrzywroc;
  const listaOtwarta = showDropdown || pokazHistorie;

  // Lista podpowiedzi idzie do `document.body`, a nie pod pole — i to nie jest kosmetyka.
  // Pasek wyszukiwania siedzi w nagłówku z `backdrop-blur-md`, a lista zwisała 195 px
  // PONIŻEJ tego nagłówka, nad mapą. Na WebKicie (iPhone: i Safari, i Chrome) potomek
  // elementu z `backdrop-filter` malowany poza jego prostokątem składa się z rozmytym
  // tłem tego elementu — czyli z mapą — i wiersze listy robią się prześwitujące. Na
  // Blinku (desktopowy Chrome) to się nie odtwarza: zmierzone tam tło listy jest w pełni
  // kryjące (rgb(251,250,249)) i lista maluje się NAD wszystkimi warstwami mapy, łącznie
  // z przyciskami `z-[1000]` i dymkami cen — czyli hipoteza „za niski z-index" jest fałszywa,
  // `isolate` w MapView już zamyka z-indeksy Leafleta w jego kontekście.
  // Wyniesienie do `body` usuwa listę z poddrzewa z rozmyciem (i przy okazji z zasięgu
  // każdego przyszłego `overflow`/`transform` nad polem), więc problem znika u źródła,
  // zamiast być przykrywany kolejnym z-indexem.
  const barRef = useRef<HTMLDivElement>(null);
  /**
   * Czy pasek jest za wąski na pełną podpowiedź. Liczy się szerokość, nie breakpoint —
   * ten sam pasek ma 569 px przy 1440 i 409 px przy 1280, bo pastylki filtrów wchodzą
   * dokładnie przy 1280 i zabierają mu miejsce.
   */
  const [waskiPasek, setWaskiPasek] = useState(false);
  const [anchor, setAnchor] = useState<{ left: number; top: number; width: number } | null>(null);

  useEffect(() => {
    const el = barRef.current;
    // `ResizeObserver` nie istnieje w jsdom — bez tej bramki testy komponentu padają na
    // konstruktorze, a nie na zachowaniu, które sprawdzają.
    if (!el || typeof ResizeObserver === 'undefined') return;
    const obserwator = new ResizeObserver(([wpis]) =>
      setWaskiPasek(wpis.contentRect.width < PROG_KROTKIEJ_PODPOWIEDZI),
    );
    obserwator.observe(el);
    return () => obserwator.disconnect();
  }, []);

  const measure = useCallback(() => {
    const r = barRef.current?.getBoundingClientRect();
    if (r) setAnchor({ left: r.left, top: r.bottom + 4, width: r.width });
  }, []);

  // Pozycja liczona przed malowaniem, żeby lista nie mrugnęła w lewym górnym rogu.
  useLayoutEffect(() => {
    if (listaOtwarta) measure();
  }, [listaOtwarta, measure]);

  // Pozycja jest `fixed`, więc przewijanie i zmiana rozmiaru muszą ją przeliczyć.
  // `capture` łapie też przewijanie kontenerów wewnętrznych, nie tylko okna.
  useEffect(() => {
    if (!listaOtwarta) return;
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [listaOtwarta, measure]);

  const add = (area: SearchArea) => {
    const wynik = dodajObszar(areas, area);
    if (wynik.limitOsiagniety) return;
    onChange(wynik.areas);
    pole.wyczysc();
    setIsOpen(false);
    setPodglad(null);
    setPodgladMiejsca(null);
    setAktywny(-1);
  };

  /** Miejsce z wyników: ten sam obszar i ta sama lista „Ostatnio wybrane”, co w „Blisko do…”. */
  const wybierzMiejsce = (p: PodpowiedzMiejsca) => {
    add(obszarZMiejsca(p));
    ostatnieMiejsca.zapamietaj(p);
  };

  const szukajMiejsca = () => {
    pole.szukajMiejsca();
    setAktywny(-1);
    setPodglad(null);
  };
  const wrocDoObszarow = () => {
    pole.wroc();
    setAktywny(-1);
    setPodgladMiejsca(null);
  };

  // Pozycje, po których chodzą strzałki — ta sama kolejność, co wiersze na ekranie.
  const pozycje: { dostepna: boolean; wybierz: () => void }[] = pole.trybMiejsca
    ? [
        { dostepna: true, wybierz: wrocDoObszarow },
        ...pole.places.map((p) => ({ dostepna: true, wybierz: () => wybierzMiejsce(p) })),
      ]
    : [
        ...suggestions.map((s) => {
          const dostepna = !s.disabled && !s.wynik.limitOsiagniety;
          return { dostepna, wybierz: () => dostepna && add(s.area) };
        }),
        ...(pole.wierszMiejsca ? [{ dostepna: true, wybierz: szukajMiejsca }] : []),
      ];
  const idOpcji = (i: number) => `pole-obszaru-opcja-${i}`;

  const naKlawisz = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setAktywny((a) => przesunAktywny(a, pozycje.map((p) => p.dostepna), e.key === 'ArrowDown' ? 1 : -1));
    } else if (e.key === 'Enter' && aktywny >= 0 && pozycje[aktywny]?.dostepna) {
      e.preventDefault();
      pozycje[aktywny].wybierz();
    }
  };

  // Podświetlona pozycja zostaje w widoku listy o stałej wysokości (jsdom nie ma scrollIntoView).
  useEffect(() => {
    if (aktywny >= 0) document.getElementById(idOpcji(aktywny))?.scrollIntoView?.({ block: 'nearest' });
  }, [aktywny]);

  const remove = (key: string) => onChange(areas.filter((a) => areaKey(a) !== key));

  // Telefon: pole NIE jest jednym wielkim przyciskiem. Było — i wtedy chipy obszarów
  // nie mogły mieć krzyżyków, bo przycisk w przycisku to nieprawidłowy HTML. Zdjęcie
  // obszaru wymagało wejścia w nakładkę i Piotr zgłosił dokładnie to pytanie: „jak mam
  // teraz czyścić filtry?". Teraz pasek jest KONTENEREM, a w środku stoi rodzeństwo:
  // przycisk otwierający nakładkę (lupa + „Warszawa" + wolna przestrzeń na końcu) oraz
  // chipy, z których każdy ma własny krzyżyk. Klawiatury dalej nie wywołujemy — pisanie
  // zaczyna się dopiero w nakładce.
  //
  // Chipy PRZEWIJAJĄ SIĘ W POZIOMIE, nie zawijają i nie chowają się pod „+N":
  //  - zawijanie rozpycha nagłówek w pionie, a nagłówek jest przyklejony i właśnie
  //    odzyskaliśmy w nim 42 px na rzędzie chipów filtrów; oddawanie ich tutaj byłoby
  //    kręceniem się w kółko,
  //  - „+N" chowa obszary, których nie da się wtedy zdjąć jednym dotknięciem — czyli
  //    nie rozwiązuje zgłoszenia, tylko przesuwa je na trzeci i czwarty obszar,
  //  - przewijanie nie jest tu nowym pomysłem: rząd chipów filtrów bezpośrednio pod
  //    tym paskiem przewija się poziomo od początku, więc to ten sam gest w tym samym
  //    miejscu ekranu.
  // Przy jednym i dwóch krótkich obszarach nic się nie przewija — pasek wygląda tak
  // samo jak wcześniej, tylko z krzyżykami.
  if (trybNakladki) {
    return (
      <>
        <ScrollableRow
          testId="pasek-obszarow"
          className="flex w-full min-h-12 flex-nowrap items-center gap-1.5 rounded-2xl border bg-white px-3 py-2"
          zaokraglone
        >
          <button
            type="button"
            onClick={() => setOverlayOpen(true)}
            aria-label={t('search.area.open')}
            className="flex shrink-0 items-center gap-1.5 text-left"
          >
            <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
            {/* „Warszawa" bez krzyżyka celowo: to nie jest obszar z listy, tylko zasięg
                produktu, którego dziś nie da się zdjąć. Na pulpicie krzyżyk przy niej
                otwiera okienko o zasięgu — na telefonie to samo objaśnienie jest w
                nakładce, a krzyżyk, który niczego nie usuwa, stojąc obok krzyżyków,
                które usuwają, kłamałby. */}
            <span className="flex shrink-0 items-center rounded-full border border-dashed px-3 py-1.5 text-sm font-medium text-muted-foreground">
              {nazwaMiasta}
            </span>
          </button>
          {areas.map((a) => (
            <AreaChip
              key={areaKey(a)}
              area={a}
              onRemove={() => remove(areaKey(a))}
              onBody={() => setOverlayOpen(true)}
              blysk={blysk === areaKey(a)}
              t={t}
            />
          ))}
          {/* Reszta szerokości też otwiera nakładkę: puste miejsce w polu wyszukiwania
              wygląda jak zaproszenie do dotknięcia i ludzie w nie celują. Zachęta
              tekstowa pokazuje się tylko wtedy, gdy nie ma ani jednego obszaru. */}
          <button
            type="button"
            onClick={() => setOverlayOpen(true)}
            // Bez obszarow nazwe daje widoczna zacheta; z obszarami przycisk jest pusty,
            // wiec musi ja dostac jawnie. Dwa przyciski o tej samej nazwie w jednym polu
            // sa nie do rozroznienia i dla czytnika, i dla testu.
            aria-label={areas.length ? t('search.area.add') : undefined}
            className="min-w-12 flex-1 truncate py-1.5 text-left text-base text-muted-foreground"
          >
            {areas.length === 0 ? t('search.area.placeholder') : ''}
          </button>
          {naWyczyscLokalizacje && (
          <button
            type="button"
            data-testid="usun-lokalizacje-pasek"
            // × zdejmuje całą lokalizację naraz (obszary, dzielnice, narysowany kształt), filtry
            // zostają. Nie otwiera nakładki — stąd `stopPropagation`, jak w krzyżyku chipa.
            onClick={(e) => { e.stopPropagation(); naWyczyscLokalizacje(); }}
            aria-label={t('search.area.clear_location')}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        </ScrollableRow>
        {overlayOpen && (
          <SearchAreaOverlay
            areas={areas}
            onChange={onChange}
            onClose={() => setOverlayOpen(false)}
            onPrzywroc={onPrzywroc}
            onDrawArea={onDrawArea}
            onPickDistricts={onPickDistricts}
            filters={filters}
            szukajMiejsc={szukajMiejsc}
          />
        )}
      </>
    );
  }

  return (
    <div className="relative" ref={barRef}>
      <div className="flex flex-nowrap items-center gap-1.5 overflow-hidden rounded-2xl border bg-white px-3 py-2 lg:gap-2 lg:px-4 lg:py-2.5">
        <Search className="h-4 w-4 shrink-0 text-muted-foreground lg:h-5 lg:w-5" />
        {/* Default scope chip: every search is Warsaw today. The X exists for
            function parity — clicking it opens the coverage dialog
            (becomes a real removal once multi-city ships). */}
        <span className="flex shrink-0 items-center gap-1 rounded-full border border-dashed px-3 py-1.5 text-sm font-medium text-muted-foreground lg:gap-1.5">
          {nazwaMiasta}
          <button
            type="button"
            aria-label={t('search.area.remove', { name: nazwaMiasta })}
            onClick={() => setWarsawInfoOpen(true)}
            className="hover:text-foreground"
          >
            <X className="h-3 w-3 lg:h-3.5 lg:w-3.5" />
          </button>
        </span>
        {areas.slice(0, MAX_INLINE_AREAS).map((a) => (
          <AreaChip
            key={areaKey(a)}
            area={a}
            onRemove={() => remove(areaKey(a))}
            sciskalny
            przygaszony={przygaszone.has(areaKey(a))}
            blysk={blysk === areaKey(a)}
            t={t}
          />
        ))}
        {/* Overflow collapses into a "+N" chip; the popover lists ALL selected
            areas with remove, so the bar stays one row no matter how many. */}
        {areas.length > MAX_INLINE_AREAS && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex shrink-0 items-center rounded-full bg-muted px-3 py-1.5 text-sm font-medium hover:bg-muted/70"
                aria-label={t('search.area.more', { count: areas.length - MAX_INLINE_AREAS })}
              >
                +{areas.length - MAX_INLINE_AREAS}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="max-h-72 w-64 overflow-y-auto">
              <p className="mb-2 text-xs font-medium text-muted-foreground">
                {t('search.area.selected_title')}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {areas.map((a) => (
                  <AreaChip
                    key={areaKey(a)}
                    area={a}
                    onRemove={() => remove(areaKey(a))}
                    przygaszony={przygaszone.has(areaKey(a))}
                    blysk={blysk === areaKey(a)}
                    t={t}
                  />
                ))}
              </div>
            </PopoverContent>
          </Popover>
        )}
        <Input
          value={query}
          onChange={(e) => {
            pole.pisz(e.target.value);
            setIsOpen(true);
            setAktywny(-1);
          }}
          onKeyDown={naKlawisz}
          onFocus={() => {
            setHistoria(czytajHistorie());
            setIsOpen(true);
          }}
          onBlur={() => setTimeout(() => setIsOpen(false), 150)}
          // PODPOWIEDŹ KRÓTSZA, GDY PASEK JEST WĄSKI. Mierzymy pasek, a nie breakpoint:
          // przy 1280 px pastylki filtrów już są (xl), więc na pasek zostaje 409 px zamiast
          // 569 i pełne zdanie urywa się w połowie słowa („…lub stacja metr") — podpowiedzi
          // przeglądarka nie skraca wielokropkiem. Próg wzięty z pomiaru obu szerokości.
          placeholder={
            areas.length
              ? t('search.area.add')
              : t(waskiPasek ? 'search.area.placeholder_short' : 'search.area.placeholder')
          }
          // text-base, nie text-sm, i to na KAŻDEJ szerokości: iPhone powiększa całą
          // stronę przy fokusie na polu poniżej 16 px i już tego nie cofa (zgłoszenie
          // Piotra 26.09 — logo urwane do „at hero", mapa uciekająca w bok). Wcześniej
          // było `text-sm lg:text-base`, czyli dokładnie na odwrót: małe tam, gdzie musi
          // być duże. Pilnuje tego src/lib/mobileInputZoom.guard.test.ts.
          // appearance-none + bg-transparent: Safari rysuje polu type=search własną zaokrągloną
          // ramkę, a tło `bg-background` odcinało się od białego paska — na pulpicie wyglądało to
          // jak drugie, „przerwane” pole w pasku (zgłoszenie Maćka 20.10).
          className="h-9 min-w-[3rem] flex-1 appearance-none rounded-none border-0 bg-transparent px-1 text-base shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
          // type=search + these turn off iOS Safari's AutoFill accessory bar
          // (Passwords / Contacts / Credit-card icons) that otherwise sits above
          // the keyboard — iOS doesn't offer credential/contact autofill on a
          // search field. role=combobox marks it app-managed so the browser keeps
          // its own autofill out of the way.
          type="search"
          name="area-search"
          role="combobox"
          aria-expanded={showDropdown}
          aria-autocomplete="list"
          aria-controls={showDropdown ? 'area-suggestions' : undefined}
          aria-activedescendant={showDropdown && aktywny >= 0 ? idOpcji(aktywny) : undefined}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="search"
          // Opt this field out of password-manager autofill bars (iOS Passwords /
          // 1Password / LastPass / Bitwarden / Dashlane) — the 🔑💳📍 bar above the
          // keyboard is theirs, and they respect these per-field ignore flags.
          data-1p-ignore="true"
          data-lpignore="true"
          data-bwignore="true"
          data-form-type="other"
        />
        {/* „DZIELNICE” — wejście do kaskady dzielnic i osiedli (plan §1.17, E2.2) obok pola do
            pisania: pisanie zostaje drogą dla tych, którzy wiedzą, czego szukają; lista jest dla
            tych, którzy chcą zobaczyć, co jest. Edytuje filtry NA ŻYWO, jak dymki Cena i Pokoje
            obok — „Gotowe” tylko zamyka. Ten sam komponent stoi w nakładce na telefonie i w
            wierszu Lokalizacja panelu. */}
        {/* Toast nigdy nie leży na wyborze dzielnic (plan §1.25): otwarcie zamyka komunikat na ekranie. */}
        <Popover open={dzielniceOtwarte} onOpenChange={(o) => { if (o) toast.dismiss(); setDzielniceOtwarte(o); }}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid="przycisk-dzielnice"
              className={`flex h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium transition-colors hover:bg-muted ${
                dzielniceOtwarte ? 'bg-muted text-foreground' : 'text-muted-foreground'
              }`}
            >
              <MapPin className="h-4 w-4" />
              {t('search.area.districts_button')}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[26rem] max-w-[calc(100vw-2rem)] p-3">
            <p className="mb-2 px-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('search.area.districts_section')}
            </p>
            <div className="max-h-[65vh] overflow-y-auto">
              <WyborDzielnic areas={areas} onChange={onChange} onPodglad={setPodgladDzielnic} />
            </div>
            <div className="mt-2 flex justify-end border-t pt-2">
              <Button type="button" variant="ghost" size="sm" onClick={() => setDzielniceOtwarte(false)}>
                {t('search.area.done')}
              </Button>
            </div>
          </PopoverContent>
        </Popover>
        {naWyczyscLokalizacje && (
          // Tooltip tylko tu — to gałąź pulpitu z myszą (hover, fokus z klawiatury). Na dotyku
          // hovera nie ma; wyjaśnia komunikat z „Cofnij" po dotknięciu (SearchPage).
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                data-testid="usun-lokalizacje-pasek"
                onClick={(e) => { e.stopPropagation(); naWyczyscLokalizacje(); }}
                aria-label={t('search.area.clear_location')}
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {t('search.area.clear_location_hint', { miasto: t(`search.area.miasto_biernik.${PRIMARY_ACTIVE_CITY}`, { defaultValue: PRIMARY_ACTIVE_CITY }) })}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {pokazHistorie && anchor && createPortal(
        <div
          data-testid="historia-pod-polem"
          onMouseDown={(e) => e.preventDefault()}
          className="fixed z-[60] overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
          style={{ left: anchor.left, top: anchor.top, width: anchor.width, maxHeight: '22rem' }}
        >
          <p className="px-3 pb-1 pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t('search.area.recent')}
          </p>
          <HistoriaWyszukiwan
            zwarta
            wpisy={historia}
            naWybierz={(w) => { setIsOpen(false); onPrzywroc?.(filtryZWpisu(w)); }}
            naUsun={(q) => setHistoria(usunWpis(q))}
            naWyczysc={() => setHistoria(wyczyscHistorie())}
          />
        </div>,
        document.body,
      )}
      {showDropdown && anchor && createPortal(
        <div
          id="area-suggestions"
          data-testid="area-suggestions"
          onMouseDown={(e) => e.preventDefault()}
          // `bg-popover` zamiast `bg-background`: to jest ten sam token, na którym
          // stoją wszystkie pozostałe warstwy unoszone w tej aplikacji, i jest kryjący.
          // z-[60]: nad nagłówkiem wyszukiwarki (z-30) i nad każdą warstwą mapy, a pod
          // oknami modalnymi. Bez `backdrop-blur` — warstwa ma zasłaniać, nie przepuszczać.
          className="fixed z-[60] overflow-y-auto rounded-md border border-border bg-popover text-popover-foreground shadow-lg"
          style={{ left: anchor.left, top: anchor.top, width: anchor.width, maxHeight: '16rem' }}
        >
          {pole.trybMiejsca ? (
            // TRYB MIEJSC (plan §1.90): w miejscu podpowiedzi dzielnic — „wróć”, wyniki, atrybucja.
            <ListaMiejscWPolu
              places={pole.places}
              status={pole.status}
              laduje={pole.laduje}
              aktywny={aktywny}
              idPrefix="pole-obszaru-opcja"
              onWroc={wrocDoObszarow}
              onWybierz={wybierzMiejsce}
              onPodglad={setPodgladMiejsca}
            />
          ) : (
          <>
          {ponadLimit && (
            <p role="status" className="bg-warning-soft px-3 py-2 text-xs text-warning-soft-foreground">
              {t('search.area.limit_reached', { count: MAX_OBSZAROW })}
            </p>
          )}
          {suggestions.length === 0 ? (
            // Bez dawnego „Wkrótce wyszukasz też po adresie.” — tę obietnicę spełnia wiersz
            // „Szukaj miejsca” poniżej (plan §1.90).
            <div className="px-3 py-2 text-sm text-muted-foreground">
              {t('search.area.no_results')}
            </div>
          ) : (
            suggestions.map((s, i) => {
              // Podpowiedź, która przekroczyłaby limit 8, jest wyszarzona od razu — nie dopiero po
              // kliknięciu. Osiedle, które ZASTĘPUJE wybraną dzielnicę, limitu nie przekracza i działa.
              const niedostepna = s.disabled || s.wynik.limitOsiagniety;
              return (
              <button
                key={areaKey(s.area)}
                id={idOpcji(i)}
                type="button"
                disabled={niedostepna}
                data-aktywny={aktywny === i || undefined}
                onClick={() => !niedostepna && add(s.area)}
                onMouseEnter={() => setPodglad(niedostepna ? null : areaKey(s.area))}
                onMouseLeave={() => setPodglad((p) => (p === areaKey(s.area) ? null : p))}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                  niedostepna ? 'cursor-not-allowed opacity-50' : 'hover:bg-muted'
                } ${aktywny === i ? 'bg-muted' : ''}`}
              >
                {s.type === 'city' ? (
                  <MapPin className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : s.type === 'district' ? (
                  <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                ) : s.type === 'station' ? (
                  <WarsawMetroIcon size={16} />
                ) : (
                  <Home className="h-4 w-4 shrink-0 text-muted-foreground" />
                )}
                <span className="min-w-0 flex-1 truncate">
                  {nazwaObszaru(s.area)}
                  {s.sublabel && <span className="text-muted-foreground"> · {s.sublabel}</span>}
                </span>
                <span className="shrink-0 text-[11px] text-muted-foreground">
                  {s.disabled ? t('search.area.city_soon') : t(`search.area.${s.type}`)}
                </span>
              </button>
              );
            })
          )}
          {suggestions.some((s) => s.type === 'station') && (
            <div className="flex items-center gap-1.5 border-t px-3 py-1.5 text-[11px] text-muted-foreground">
              <WarsawMetroIcon size={14} />
              {t('search.area.station_hint')}
            </div>
          )}
          {/* ZAWSZE ostatni wiersz od 3 znaków — także gdy dzielnice pasują („Politechnika” to
              stacja i uczelnia). Nic nie wyszukuje, dopóki ktoś go nie wybierze. */}
          {pole.wierszMiejsca && (
            <WierszSzukajMiejsca
              query={query}
              id={idOpcji(suggestions.length)}
              aktywny={aktywny === suggestions.length}
              onWybierz={szukajMiejsca}
              onPodglad={() => setPodglad(null)}
            />
          )}
          </>
          )}
        </div>,
        document.body,
      )}

      {/* Single-city coverage explainer (the Warszawa chip's X). */}
      <Dialog open={warsawInfoOpen} onOpenChange={setWarsawInfoOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogHeader>
            <div className="mb-1 flex justify-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-2xl">
                🏙️
              </span>
            </div>
            <DialogTitle className="text-center">
              {t('search.area.warsaw_only_title')}
            </DialogTitle>
            <DialogDescription className="text-center">
              {t('search.area.warsaw_only_body')}
            </DialogDescription>
          </DialogHeader>
          <Button className="w-full" onClick={() => setWarsawInfoOpen(false)}>
            {t('search.area.warsaw_only_cta')}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SearchAreaBar;
